<template>
  <div>
    <transition name="fade">
      <div v-if="savingOrder" class="kb-order-saving">
        <i class="fal fa-spinner fa-spin mr-2"></i>Salvando ordem...
      </div>
    </transition>

    <HeroInner descrition="Base de Conhecimento" class="mt-70" />

    <div class="grid-box">
      <v-layout bg-white ma-4 mt-5 column br-4>
        <div class="my-donations-header">
          <div>
            <Icon name="fal fa-book title-icon" size="32" colorFont="#C3C6D5" />
            <h2>Base de Conhecimento — FAQ</h2>
          </div>
          <v-btn
            flat
            round
            color="white"
            class="btn btn-primary px-4"
            large
            @click="openCreateCategory"
          >
            <i class="mr-2 fas fa-plus"></i>Nova Categoria
          </v-btn>
        </div>

        <v-tabs v-model="activeTab" class="kb-tabs" slider-color="cyan darken-1" grow>
          <v-tab v-for="opt in campaignTypeOptions" :key="opt.value">
            <i class="fal fa-tag mr-2"></i>{{ opt.label }}
          </v-tab>
        </v-tabs>
      </v-layout>

      <div class="bg-white br-4 ma-4 pa-4 kb-tree">
        <div
          class="kb-current-type"
          :class="activeTab === 0 ? 'kb-current-type--inc' : 'kb-current-type--sem'"
        >
          <i class="fas fa-circle"></i>
          Editando: <strong>{{ currentTypeLabel }}</strong>
        </div>

        <p class="kb-help text-gray-dark">
          Organize as perguntas em categorias e subcategorias. As perguntas exibidas abaixo são
          as do tipo <strong>{{ currentTypeLabel }}</strong>. Use o seletor "Somente IA" para
          esconder uma pergunta do doador, mantendo-a disponível para o agente.
        </p>

        <p v-if="categories.length === 0" class="text-gray-dark">
          Nenhuma categoria cadastrada. Comece criando uma categoria.
        </p>

        <!-- Categorias -->
        <div
          v-for="(cat, catIdx) in categories"
          :key="cat.id"
          class="kb-cat"
          :class="{ 'kb-cat--open': isCatOpen(cat.id) }"
        >
          <div class="kb-cat__head">
            <button type="button" class="kb-cat__toggle" @click="toggleCat(cat.id)">
              <i class="fal fa-chevron-down kb-cat__chevron"></i>
              <span class="kb-cat__name">{{ cat.name }}</span>
              <span class="kb-cat__badge">{{ countCategoryQuestions(cat) }}</span>
            </button>
            <div class="kb-actions">
              <v-btn
                icon
                small
                :disabled="catIdx === 0"
                @click="moveCategory(catIdx, -1)"
                title="Mover categoria para cima"
              >
                <v-icon size="16">fal fa-arrow-up</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                :disabled="catIdx === categories.length - 1"
                @click="moveCategory(catIdx, 1)"
                title="Mover categoria para baixo"
              >
                <v-icon size="16">fal fa-arrow-down</v-icon>
              </v-btn>
              <v-btn icon small @click="openCreateSubcategory(cat)" title="Nova subcategoria">
                <v-icon size="16">fal fa-plus</v-icon>
              </v-btn>
              <v-btn icon small @click="openEditCategory(cat)" title="Editar categoria">
                <v-icon size="16">fal fa-pencil</v-icon>
              </v-btn>
              <v-btn icon small @click="confirmDelete('category', cat)" title="Excluir categoria">
                <v-icon size="16">fal fa-trash</v-icon>
              </v-btn>
            </div>
          </div>

          <div v-show="isCatOpen(cat.id)" class="kb-cat__body">
            <p
              v-if="(cat.subcategories || []).length === 0"
              class="kb-empty-hint text-gray-dark"
            >
              Nenhuma subcategoria. Crie uma para adicionar perguntas.
            </p>

            <div v-for="(sub, subIdx) in cat.subcategories" :key="sub.id" class="kb-sub">
              <div class="kb-sub__head">
                <span class="kb-sub__name">{{ sub.name }}</span>
                <div class="kb-actions">
                  <v-btn
                    icon
                    small
                    :disabled="subIdx === 0"
                    @click="moveSubcategory(cat, subIdx, -1)"
                    title="Mover para cima"
                  >
                    <v-icon size="14">fal fa-arrow-up</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    :disabled="subIdx === cat.subcategories.length - 1"
                    @click="moveSubcategory(cat, subIdx, 1)"
                    title="Mover para baixo"
                  >
                    <v-icon size="14">fal fa-arrow-down</v-icon>
                  </v-btn>
                  <v-btn icon small @click="openCreateQuestion(sub)" title="Nova pergunta">
                    <v-icon size="14">fal fa-plus</v-icon>
                  </v-btn>
                  <v-btn icon small @click="openEditSubcategory(sub, cat)" title="Editar subcategoria">
                    <v-icon size="14">fal fa-pencil</v-icon>
                  </v-btn>
                  <v-btn icon small @click="confirmDelete('subcategory', sub)" title="Excluir subcategoria">
                    <v-icon size="14">fal fa-trash</v-icon>
                  </v-btn>
                </div>
              </div>

              <ul class="kb-questions">
                <li
                  v-for="(faq, faqIdx) in questionsFor(sub.id)"
                  :key="faq.id"
                  class="kb-question"
                >
                  <div class="kb-question__text">
                    <h5 :title="faq.question">{{ faq.question }}</h5>
                    <p class="text-gray-dark" :title="stripHtml(faq.answer)">{{ stripHtml(faq.answer) }}</p>
                  </div>
                  <div class="kb-question__controls">
                    <v-switch
                      :input-value="faq.isVisibleToDonor"
                      @change="toggleVisibility(faq, $event)"
                      :label="faq.isVisibleToDonor ? 'Visível ao doador' : 'Somente IA'"
                      hide-details
                      class="kb-switch"
                    />
                    <v-btn
                      icon
                      small
                      :disabled="faqIdx === 0"
                      @click="moveQuestion(sub, faqIdx, -1)"
                      title="Mover para cima"
                    >
                      <v-icon size="14">fal fa-arrow-up</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      small
                      :disabled="faqIdx === questionsFor(sub.id).length - 1"
                      @click="moveQuestion(sub, faqIdx, 1)"
                      title="Mover para baixo"
                    >
                      <v-icon size="14">fal fa-arrow-down</v-icon>
                    </v-btn>
                    <v-btn icon small @click="openEditQuestion(faq)">
                      <v-icon size="14">fal fa-pencil</v-icon>
                    </v-btn>
                    <v-btn icon small @click="confirmDelete('question', faq)">
                      <v-icon size="14">fal fa-trash</v-icon>
                    </v-btn>
                  </div>
                </li>
                <li
                  v-if="questionsFor(sub.id).length === 0"
                  class="kb-empty-hint text-gray-dark"
                >
                  Nenhuma pergunta deste tipo nesta subcategoria.
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Perguntas sem categoria (deste tipo) -->
        <div v-if="orphanQuestions.length > 0" class="kb-cat kb-cat--open kb-cat--orphan">
          <div class="kb-cat__head">
            <span class="kb-cat__name">Sem categoria</span>
            <span class="kb-cat__badge">{{ orphanQuestions.length }}</span>
          </div>
          <div class="kb-cat__body">
            <p class="kb-empty-hint text-gray-dark">
              Perguntas ainda não classificadas. Edite cada uma para atribuir categoria/subcategoria.
            </p>
            <ul class="kb-questions">
              <li v-for="faq in orphanQuestions" :key="faq.id" class="kb-question">
                <div class="kb-question__text">
                  <h5 :title="faq.question">{{ faq.question }}</h5>
                  <p class="text-gray-dark" :title="stripHtml(faq.answer)">{{ stripHtml(faq.answer) }}</p>
                </div>
                <div class="kb-question__controls">
                  <v-switch
                    :input-value="faq.isVisibleToDonor"
                    @change="toggleVisibility(faq, $event)"
                    :label="faq.isVisibleToDonor ? 'Visível ao doador' : 'Somente IA'"
                    hide-details
                    class="kb-switch"
                  />
                  <v-btn icon small @click="openEditQuestion(faq)">
                    <v-icon size="14">fal fa-pencil</v-icon>
                  </v-btn>
                  <v-btn icon small @click="confirmDelete('question', faq)">
                    <v-icon size="14">fal fa-trash</v-icon>
                  </v-btn>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <ModalKnowledgeBaseFaq ref="modalQuestion" :categories="categories" @saved="reload" />
    <ModalFaqCategory ref="modalCategory" @saved="reload" />
    <AucAlert
      ref="deleteAlert"
      :title="deleteTitle"
      confirmButtonText="Sim"
      cancelButtonText="Não"
      @confirm="executeDelete"
    />
  </div>
</template>

<script type="plain/text">
import HeroInner from "@/components/HeroInner.vue";
import AucAlert from "@/components/AucAlert.vue";
import ModalKnowledgeBaseFaq from "@/views/app/management/modal/ModalKnowledgeBaseFaq.vue";
import ModalFaqCategory from "@/views/app/management/modal/ModalFaqCategory.vue";
import KnowledgeBaseFaqService from "@/scripts/services/knowledgeBaseFaq.service";
import FaqService from "@/scripts/services/faq.service";
import { FaqCampaignTypeOptions } from "@/scripts/models/enums/faqCampaignType.enum";

export default {
  components: {
    HeroInner,
    AucAlert,
    ModalKnowledgeBaseFaq,
    ModalFaqCategory
  },
  data: () => ({
    knowledgeBaseFaqService: new KnowledgeBaseFaqService(),
    faqService: new FaqService(),
    categories: [],
    faqs: [],
    activeTab: 0,
    campaignTypeOptions: FaqCampaignTypeOptions,
    openCats: [],
    pendingDelete: null,
    savingOrder: false
  }),
  created() {
    this.reload();
  },
  computed: {
    currentType() {
      const opt = this.campaignTypeOptions[this.activeTab];
      return opt ? opt.value : null;
    },
    currentTypeLabel() {
      const opt = this.campaignTypeOptions[this.activeTab];
      return opt ? opt.label : "";
    },
    orphanQuestions() {
      return this.faqs.filter(f => !f.subcategoryId && this.matchesType(f));
    },
    deleteTitle() {
      if (!this.pendingDelete) return "";
      const map = {
        category: "Excluir esta categoria? As subcategorias e perguntas vinculadas podem ser afetadas.",
        subcategory: "Excluir esta subcategoria?",
        question: "Excluir esta pergunta da Base de Conhecimento?"
      };
      return map[this.pendingDelete.kind] || "Confirmar exclusão?";
    }
  },
  methods: {
    reload() {
      this.faqService.listAllCategories().then(data => {
        this.categories = Array.isArray(data) ? data : [];
      });
      this.knowledgeBaseFaqService.listAllFaqs().then(data => {
        this.faqs = Array.isArray(data) ? data : [];
      });
    },
    matchesType(faq) {
      return (
        Array.isArray(faq.faqCampaignTypeIds) &&
        faq.faqCampaignTypeIds.indexOf(this.currentType) !== -1
      );
    },
    questionsFor(subcategoryId) {
      return this.faqs
        .filter(f => f.subcategoryId === subcategoryId && this.matchesType(f))
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    },
    countCategoryQuestions(cat) {
      const subIds = (cat.subcategories || []).map(s => s.id);
      return this.faqs.filter(
        f => subIds.indexOf(f.subcategoryId) !== -1 && this.matchesType(f)
      ).length;
    },
    isCatOpen(catId) {
      return this.openCats.indexOf(catId) !== -1;
    },
    toggleCat(catId) {
      const i = this.openCats.indexOf(catId);
      if (i === -1) this.openCats.push(catId);
      else this.openCats.splice(i, 1);
    },
    // ---- Categoria / Subcategoria ----
    openCreateCategory() {
      this.$refs.modalCategory.open("category", null);
    },
    // Reordena categorias com setas: move uma posição e reindexa o orderIndex (1..N),
    // otimista e sem bloquear a tela; salva só as que mudaram.
    async moveCategory(catIdx, direction) {
      const target = catIdx + direction;
      if (target < 0 || target >= this.categories.length) return;

      const [moved] = this.categories.splice(catIdx, 1);
      this.categories.splice(target, 0, moved);

      const toSave = [];
      this.categories.forEach((cat, idx) => {
        const newOrder = idx + 1;
        if (cat.orderIndex !== newOrder) {
          this.$set(cat, "orderIndex", newOrder);
          toSave.push({
            id: cat.id,
            name: cat.name,
            orderIndex: newOrder,
            active: cat.active !== false
          });
        }
      });
      if (toSave.length === 0) return;

      this.savingOrder = true;
      try {
        for (const cat of toSave) {
          await this.faqService.saveCategory(cat, true);
        }
      } catch (e) {
        this.$store.commit("SET_SNACKBAR_MESSAGE", "Não foi possível salvar a nova ordem.");
        this.reload();
      } finally {
        this.savingOrder = false;
      }
    },
    openEditCategory(cat) {
      this.$refs.modalCategory.open("category", cat);
    },
    openCreateSubcategory(cat) {
      if (!this.isCatOpen(cat.id)) this.toggleCat(cat.id);
      this.$refs.modalCategory.open("subcategory", null, cat);
    },
    openEditSubcategory(sub, cat) {
      this.$refs.modalCategory.open("subcategory", sub, cat);
    },
    // Reordena subcategorias com setas: move uma posição e reindexa o orderIndex (1..N),
    // salvando apenas as subcategorias cujo orderIndex mudou.
    async moveSubcategory(cat, subIdx, direction) {
      const subs = cat.subcategories || [];
      const target = subIdx + direction;
      if (target < 0 || target >= subs.length) return;

      // Otimista: reordena o array reativo na hora (UI imediata).
      const [moved] = subs.splice(subIdx, 1);
      subs.splice(target, 0, moved);

      const toSave = [];
      subs.forEach((s, idx) => {
        const newOrder = idx + 1;
        if (s.orderIndex !== newOrder) {
          this.$set(s, "orderIndex", newOrder);
          toSave.push({
            id: s.id,
            categoryId: cat.id,
            name: s.name,
            orderIndex: newOrder,
            active: s.active !== false
          });
        }
      });
      if (toSave.length === 0) return;

      // Salva em background, silencioso (sem overlay bloqueante), sequencial.
      this.savingOrder = true;
      try {
        for (const s of toSave) {
          await this.faqService.saveSubcategory(s, true);
        }
      } catch (e) {
        this.$store.commit("SET_SNACKBAR_MESSAGE", "Não foi possível salvar a nova ordem.");
        this.reload();
      } finally {
        this.savingOrder = false;
      }
    },
    // ---- Perguntas ----
    openCreateQuestion(sub) {
      this.$refs.modalQuestion.open(null, {
        subcategoryId: sub.id,
        campaignType: this.currentType
      });
    },
    openEditQuestion(faq) {
      this.$refs.modalQuestion.open(faq);
    },
    // Reordena perguntas dentro da subcategoria (campo sortOrder) com setas.
    // Reindexa sequencialmente as perguntas visíveis do tipo atual e salva as que mudaram.
    async moveQuestion(sub, faqIdx, direction) {
      const faqs = this.questionsFor(sub.id);
      const target = faqIdx + direction;
      if (target < 0 || target >= faqs.length) return;

      // Otimista: reordena e reindexa sortOrder nos proprios objetos (questionsFor reordena na hora).
      const ordered = faqs.slice();
      const [moved] = ordered.splice(faqIdx, 1);
      ordered.splice(target, 0, moved);

      const toSave = [];
      ordered.forEach((faq, idx) => {
        const newOrder = idx + 1;
        if (faq.sortOrder !== newOrder) {
          this.$set(faq, "sortOrder", newOrder);
          toSave.push({
            id: faq.id,
            question: faq.question,
            answer: faq.answer,
            isVisibleToDonor: faq.isVisibleToDonor,
            sortOrder: newOrder,
            faqCampaignTypeIds: faq.faqCampaignTypeIds,
            subcategoryId: faq.subcategoryId
          });
        }
      });
      if (toSave.length === 0) return;

      // Salva em background, silencioso (sem overlay bloqueante), sequencial.
      this.savingOrder = true;
      try {
        for (const faq of toSave) {
          await this.knowledgeBaseFaqService.save(faq, true);
        }
      } catch (e) {
        this.$store.commit("SET_SNACKBAR_MESSAGE", "Não foi possível salvar a nova ordem.");
        this.reload();
      } finally {
        this.savingOrder = false;
      }
    },
    toggleVisibility(faq, value) {
      this.knowledgeBaseFaqService
        .save({
          id: faq.id,
          question: faq.question,
          answer: faq.answer,
          isVisibleToDonor: value,
          sortOrder: faq.sortOrder,
          faqCampaignTypeIds: faq.faqCampaignTypeIds,
          subcategoryId: faq.subcategoryId
        })
        .then(this.reload);
    },
    // ---- Exclusão ----
    confirmDelete(kind, item) {
      this.pendingDelete = { kind, item };
      this.$refs.deleteAlert.open();
    },
    executeDelete() {
      if (!this.pendingDelete) return;
      const { kind, item } = this.pendingDelete;
      let request;
      if (kind === "category") request = this.faqService.deleteCategory(item.id);
      else if (kind === "subcategory") request = this.faqService.deleteSubcategory(item.id);
      else request = this.knowledgeBaseFaqService.deleteFaq(item.id);

      request.then(() => {
        this.pendingDelete = null;
        this.reload();
      });
    },
    // Remove tags HTML pro preview/tooltip (a resposta pode ser HTML do editor).
    stripHtml(html) {
      return (html || "").replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
    }
  }
};
</script>

<style scoped>
.kb-order-saving {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  background: #4c4d4f;
  color: #fff;
  font-size: 13px;
  padding: 8px 16px;
  border-radius: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.kb-help {
  font-size: 13px;
  margin-bottom: 16px;
}
.kb-tabs {
  margin-top: 8px;
}

/* Aba ativa bem evidente */
.kb-tabs /deep/ .v-tabs__item {
  font-weight: 500;
  font-size: 15px;
  color: #80849a;
  text-transform: none;
  opacity: 1;
}
.kb-tabs /deep/ .v-tabs__item--active {
  color: #02acb4;
  font-weight: 700;
  background: rgba(2, 172, 180, 0.08);
  border-bottom: 3px solid #02acb4;
}
.kb-tabs /deep/ .v-tabs__slider {
  background-color: #02acb4;
  height: 3px;
}

/* Indicador do tipo em edição (cores distintas por tipo) */
.kb-current-type {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  padding: 6px 14px;
  border-radius: 20px;
  margin-bottom: 14px;
}
.kb-current-type i {
  font-size: 10px;
}
.kb-current-type strong {
  font-weight: 700;
}
.kb-current-type--inc {
  background: rgba(21, 101, 192, 0.1);
  color: #1565c0;
}
.kb-current-type--sem {
  background: rgba(123, 31, 162, 0.1);
  color: #7b1fa2;
}

/* Categoria */
.kb-cat {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  margin-bottom: 10px;
  overflow: hidden;
}
.kb-cat--open {
  border-color: #02acb4;
}
.kb-cat__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: #fafbfc;
}
.kb-cat__toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
}
.kb-cat__chevron {
  color: #999;
  transition: transform 0.2s;
}
.kb-cat--open .kb-cat__chevron {
  transform: rotate(180deg);
}
.kb-cat__name {
  font-weight: 600;
  font-size: 15px;
  color: #4c4d4f;
}
.kb-cat__badge {
  background: rgba(2, 172, 180, 0.12);
  color: #02acb4;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 9px;
  border-radius: 12px;
  min-width: 24px;
  text-align: center;
}
.kb-cat__body {
  padding: 8px 16px 16px;
}
.kb-cat--orphan {
  border-style: dashed;
  border-color: #e0a800;
}

/* Subcategoria */
.kb-sub {
  margin-top: 14px;
}
.kb-sub__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 4px;
  margin-bottom: 6px;
}
.kb-sub__name {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #02acb4;
}

/* Perguntas */
.kb-questions {
  list-style: none;
  margin: 0;
  padding: 0;
}
.kb-question {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f5f5f5;
  min-height: 72px;
}
.kb-question__text {
  flex: 1;
  min-width: 0;
}
.kb-question__text h5 {
  margin: 0 0 4px;
  font-size: 14px;
  color: #4c4d4f;
  /* limita a 2 linhas pra alinhar independente do tamanho da pergunta */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.kb-question__text p {
  margin: 0;
  font-size: 13px;
  /* limita a 2 linhas com reticências pra manter altura consistente */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.kb-question__controls {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.kb-switch {
  margin: 0;
  padding: 0;
}
.kb-actions {
  display: flex;
  gap: 2px;
  flex-shrink: 0;
}
.kb-empty-hint {
  font-size: 13px;
  font-style: italic;
  padding: 6px 0;
}
</style>
